import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getGroupTours } from '../../api/groupToursApi';
import { loadJourneysPage } from '../../utils/cmsContentLoader';
import { isGroupBookingEnabled } from '../../utils/journeyBookingFlags';
import { buildMonthRange, monthLabelFromValue } from '../../helpers/buildMonthRange';
import getDepartureCardCopy, { TRAVELLER_STATUS } from '../../utils/getDepartureCardCopy';
import DepartureTravellerSummary from './DepartureTravellerSummary';

function tourMonth(tour) {
	return tour.departure_month || (tour.start_date ? String(tour.start_date).slice(0, 7) : null);
}

function tourItem(tour) {
	const copy = getDepartureCardCopy(tour);
	if (!copy.canReserve && copy.kind !== TRAVELLER_STATUS.GUARANTEED) return null;
	if (copy.kind === TRAVELLER_STATUS.OPEN && !tour.journey_slug) return null;
	if (copy.paidReservations === 0 && copy.kind === TRAVELLER_STATUS.OPEN) return null;
	return { tour, copy, month: tourMonth(tour) };
}

function GroupsFormingNow({ onJoinPlatformDeparture }) {
	const history = useHistory();
	const [tours, setTours] = useState([]);
	const [journeyFlags, setJourneyFlags] = useState({});
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const months = buildMonthRange(12).join(',');
		setLoading(true);
		Promise.all([
			getGroupTours({ status: 'available', year_month: months, per_page: 60 }),
			loadJourneysPage({ per_page: 100 }),
		])
			.then(([toursRes, journeysRes]) => {
				setTours(toursRes.data?.group_tours || []);
				const flags = {};
				(journeysRes.journeys || []).forEach((j) => {
					flags[j.slug] = isGroupBookingEnabled(j);
				});
				setJourneyFlags(flags);
			})
			.catch(() => {
				setTours([]);
				setJourneyFlags({});
			})
			.finally(() => setLoading(false));
	}, []);

	const { forming, confirmed } = useMemo(() => {
		const formingList = [];
		const confirmedList = [];
		tours.forEach((tour) => {
			if (!tour.journey_slug) return;
			if (journeyFlags[tour.journey_slug] === false) return;
			const item = tourItem(tour);
			if (!item) return;
			if (item.copy.kind === TRAVELLER_STATUS.GUARANTEED) confirmedList.push(item);
			else formingList.push(item);
		});
		const sortFn = (a, b) => {
			if (b.copy.progress !== a.copy.progress) return b.copy.progress - a.copy.progress;
			return (a.month || '').localeCompare(b.month || '');
		};
		return {
			forming: formingList.sort(sortFn),
			confirmed: confirmedList.sort(sortFn),
		};
	}, [tours, journeyFlags]);

	const handleJoin = (item) => {
		const { tour, month } = item;
		if (!tour.journey_slug || !month) return;
		if (onJoinPlatformDeparture) {
			onJoinPlatformDeparture({ journeySlug: tour.journey_slug, yearMonth: month, tour });
			return;
		}
		history.push(`/group-trips?journey=${encodeURIComponent(tour.journey_slug)}&month=${encodeURIComponent(month)}`);
		setTimeout(() => {
			document.getElementById('gd-reserve-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}, 80);
	};

	if (loading) {
		return (
			<section className="gd-forming-now gd-forming-now--loading" aria-busy="true">
				<p className="gd-forming-now__loading">Finding departures travellers are joining…</p>
			</section>
		);
	}

	if (forming.length === 0 && confirmed.length === 0) {
		return (
			<section className="gd-forming-now gd-forming-now--empty">
				<div className="gd-forming-now__head">
					<h2 className="gd-forming-now__title">Be the first on a new departure</h2>
					<p className="gd-forming-now__lead">
						No groups are gathering yet for the next few months. Pick a trek and month below — your deposit starts the group.
					</p>
				</div>
			</section>
		);
	}

	const renderCard = (item) => {
		const { tour, copy, month } = item;
		const almostFull = tour.seats_remaining != null
			? tour.seats_remaining > 0 && tour.seats_remaining <= 2
			: tour.spots_left != null && tour.spots_left > 0 && tour.spots_left <= 2;

		return (
			<article key={`${tour.idx}-${month}`} className={`gd-forming-card gd-forming-card--${copy.kind}`}>
				{almostFull && copy.canReserve && (
					<span className="gd-forming-card__badge">Almost full</span>
				)}
				<DepartureTravellerSummary
					departure={tour}
					title={tour.name || tour.from_to_display || 'Group departure'}
					subtitle={
						<>
							<i className="fas fa-calendar-alt" aria-hidden="true" />
							{monthLabelFromValue(month)}
							{tour.dates_display ? <span className="gd-forming-card__dates"> · {tour.dates_display}</span> : null}
						</>
					}
					compact
					className="gd-forming-card__summary"
				/>
				<button
					type="button"
					className="gd-forming-card__cta"
					onClick={() => handleJoin(item)}
					disabled={!copy.canReserve}
				>
					{copy.reserveCta}
				</button>
			</article>
		);
	};

	return (
		<section className="gd-forming-now" aria-label="Active group departures">
			<div className="gd-forming-now__head">
				<h2 className="gd-forming-now__title">Groups happening now</h2>
				<p className="gd-forming-now__lead">
					See which departures are forming and which are already guaranteed. Reserve with a small deposit — pay the balance after the group confirms.
				</p>
			</div>

			{forming.length > 0 && (
				<div className="gd-forming-now__block">
					<h3 className="gd-forming-now__subtitle">
						<span className="gd-forming-now__dot gd-forming-now__dot--forming" aria-hidden="true" />
						Forming — help guarantee the departure
					</h3>
					<div className="gd-forming-now__grid">{forming.map(renderCard)}</div>
				</div>
			)}

			{confirmed.length > 0 && (
				<div className="gd-forming-now__block">
					<h3 className="gd-forming-now__subtitle">
						<span className="gd-forming-now__dot gd-forming-now__dot--confirmed" aria-hidden="true" />
						Guaranteed — this trip will run
					</h3>
					<div className="gd-forming-now__grid">{confirmed.map(renderCard)}</div>
				</div>
			)}
		</section>
	);
}

export default GroupsFormingNow;
